import { lazy, type ComponentType } from "react";
import { reloadOnceForStaleChunks } from "@/shared/lib/stale-chunk-reload";

type LazyModule<T extends ComponentType<any>> = {
  default?: T;
};

/**
 * A deployment can replace Vite's hashed chunks while an older app shell is
 * still open. Reload once to obtain a matching index.html and asset set.
 */
export function lazyWithReload<T extends ComponentType<any>>(
  load: () => Promise<LazyModule<T>>,
  key: string,
) {
  return lazy(async () => {
    try {
      const module = await load();

      if (!module.default) {
        throw new Error(`Lazy route "${key}" did not export a component.`);
      }

      sessionStorage.removeItem(`fysaro:lazy-reload:${key}`);
      return { default: module.default };
    } catch (error) {
      const message =
        error instanceof Error ? error.message : String(error ?? "");
      const reloadKey = `fysaro:lazy-reload:${key}`;
      const lastReloadAt = Number(sessionStorage.getItem(reloadKey) ?? 0);
      const isStaleChunk =
        message.includes("did not export") ||
        message.toLowerCase().includes("mime type") ||
        message.toLowerCase().includes("dynamically imported module");

      if (
        isStaleChunk ||
        Date.now() - lastReloadAt >= 10_000
      ) {
        sessionStorage.setItem(reloadKey, String(Date.now()));
        if (
          reloadOnceForStaleChunks(
            `lazy-route:${key}:${message}`,
          )
        ) {
          return new Promise<never>(() => undefined);
        }
      }

      throw error;
    }
  });
}
